import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import templates from "../data/templates";
import ConfettiEffect from "../components/ConfettiEffect";
import SparkleEffect from "../components/SparkleEffect";
import BalloonEffect from "../components/BalloonEffect";
import { QRCodeSVG } from "qrcode.react";
import FloatingMessages from "../components/FloatingMessages";
import EventSlideshow from "../components/EventSlideshow";
import { findEventByCodeOrSlug, getEventPublicCode } from "../utils/eventLookup";

const defaultDisplaySettings = {
  accentColor: "#ec4899",
  effectIntensity: "normal",
  slideshowLayout: "three-photo",
  qrPosition: "bottom-right",
  messageStyle: "both",
};

const getQrPanelClass = (position) => {
  const base =
    "fixed z-20 rounded-md border border-white/70 bg-white/95 p-3 text-center shadow-2xl";

  if (position === "bottom-center") {
    return `${base} bottom-4 left-1/2 w-[190px] -translate-x-1/2`;
  }

  if (position === "large-tv") {
    return `${base} bottom-6 right-6 w-[250px] p-4`;
  }

  return `${base} bottom-4 right-4 w-[160px] sm:w-[190px]`;
};

const EventWelcomePage = () => {
  const [event, setEvent] = useState(null);
  const { shortCode } = useParams();
  const [userId, setUserId] = useState(null);
  const [eventId, setEventId] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchEvent = async () => {
      const result = await findEventByCodeOrSlug(shortCode);
      if (result) {
        setEvent(result.event);
        setUserId(result.userId);
        setEventId(result.eventId);
        setNotFound(false);
      } else {
        setNotFound(true);
      }
    };
    fetchEvent();
  }, [shortCode]);

  if (notFound) {
    return (
      <div className="p-6 text-center text-gray-700">
        This event welcome page could not be found.
      </div>
    );
  }

  if (!event) return <div className="p-6 text-center">Loading...</div>;

  const selectedTemplate =
    templates.find((t) => t.id === event.templateId) || templates[0];
  const displaySettings = {
    ...defaultDisplaySettings,
    ...(event.displaySettings || {}),
  };
  const animations = event.animations || selectedTemplate.animations || {};
  const effectIntensity = displaySettings.effectIntensity;
  const showFloatingMessages =
    displaySettings.messageStyle === "both" ||
    displaySettings.messageStyle === "floating";
  const showPhotoCaptions =
    displaySettings.messageStyle === "both" ||
    displaySettings.messageStyle === "under-photo";
  const showQr = displaySettings.qrPosition !== "hidden";
  const qrSize = displaySettings.qrPosition === "large-tv" ? 180 : 112;
  const publicCode = getEventPublicCode(event, shortCode);
  const titleClass = selectedTemplate.background.includes("text-white")
    ? "text-white"
    : "text-gray-950";

  return (
    <div
      className={`relative min-h-screen ${selectedTemplate.background} overflow-hidden`}
    >
      {animations.confetti && <ConfettiEffect intensity={effectIntensity} />}
      {animations.sparkles && <SparkleEffect intensity={effectIntensity} />}
      {animations.balloons && <BalloonEffect intensity={effectIntensity} />}
      {showFloatingMessages && (
        <FloatingMessages userId={userId} eventId={eventId} />
      )}

      <div className="relative z-10 mx-auto max-w-5xl px-4 pb-2 pt-7 text-center sm:px-6 sm:pt-8 lg:px-8">
        <h1
          className={`mx-auto mb-3 max-w-4xl text-3xl font-bold leading-tight drop-shadow sm:text-5xl ${titleClass}`}
        >
          {event.welcomeMessage}
        </h1>
      </div>

      <div className="relative z-10 mx-auto max-w-5xl px-4 pb-2 sm:px-6 lg:px-8">
        <EventSlideshow
          userId={userId}
          eventId={eventId}
          layoutMode={displaySettings.slideshowLayout}
          showCaptions={showPhotoCaptions}
        />
      </div>

      {showQr && (
        <div className="relative z-10 mx-auto max-w-4xl px-4 pb-8 pt-1 text-center sm:px-6 lg:px-8">
          <h2
            className={`text-xl font-bold drop-shadow sm:text-2xl ${titleClass}`}
          >
            Want to add yours? Scan the QR code.
          </h2>
        </div>
      )}

      {showQr && (
        <div
          className={getQrPanelClass(displaySettings.qrPosition)}
          style={{ borderTop: `4px solid ${displaySettings.accentColor}` }}
        >
          <p className="text-sm font-bold text-gray-900">
            Send {event.title} a message
          </p>
          <p className="mb-2 mt-1 text-xs leading-snug text-gray-600">
            Add a photo or note
          </p>
          <QRCodeSVG
            value={`${window.location.origin}/event/${publicCode}/form`}
            size={qrSize}
            className="mx-auto"
          />
        </div>
      )}
    </div>
  );
};

export default EventWelcomePage;
